import { ColumnDef } from '@tanstack/react-table';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES } from 'src/components/Reports/ReportsTableEditor/ReportsTableEditorForm.constants';
import { useAppContext } from 'src/contexts/AppContext';
import {
  ReportMetricsResponse,
  ReportValueTypesReponse,
} from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { hasFeatures } from 'src/utils/userUtils';

import { costOfGoodsSoldReportTableColumnDef } from './configs/CostOfGoodsSoldReportTableColumnsConfig';

type CostOfGoodsSoldReportTableColumnDefContextType = {
  displayedColumnsConfig: ColumnDef<ReportMetricsResponse | null>[] | undefined;
};

const CostOfGoodsSoldReportTableColumnDefContext =
  createContext<CostOfGoodsSoldReportTableColumnDefContextType>({
    displayedColumnsConfig: undefined,
  });

export const CostOfGoodsSoldReportTableColumnDefContextV2 = ({
  report,
  reportValueTypes,
  children,
}: PropsWithChildren<{
  report: ReportConfigV2;
  reportValueTypes: ReportValueTypesReponse | undefined;
}>) => {
  const { appContext, loginContext } = useAppContext();
  const personalizationConfigOverrides = useMemo(() => {
    return (
      REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES[
        ReportTypesV2.CostOfGoodsSold
      ] ?? {}
    );
  }, []);

  const displayedColumnsConfig = useMemo(() => {
    const columns = [
      ...(report.request.rowGroupings ?? []),
      ...(report.request.aggregations ?? []),
    ];

    return columns
      .filter(({ columnName: columnId }) => {
        const personalization = personalizationConfigOverrides[columnId];

        if (personalization?.requiredFeatures?.length) {
          return hasFeatures(
            loginContext?.user,
            appContext?.features,
            personalization.requiredFeatures
          );
        }
        return true;
      })
      .map(({ columnName: columnId, columnValue }) =>
        costOfGoodsSoldReportTableColumnDef(
          columnId,
          columnValue,
          reportValueTypes?.[columnId.toUpperCase()],
          personalizationConfigOverrides[columnId]
        )
      );
  }, [
    appContext?.features,
    loginContext?.user,
    personalizationConfigOverrides,
    report.request.aggregations,
    report.request.rowGroupings,
    reportValueTypes,
  ]);

  return (
    <CostOfGoodsSoldReportTableColumnDefContext.Provider
      value={{
        displayedColumnsConfig: displayedColumnsConfig,
      }}
    >
      {children}
    </CostOfGoodsSoldReportTableColumnDefContext.Provider>
  );
};

export const useCostOfGoodsSoldReportTableColumnDefV2 = () =>
  useContext(CostOfGoodsSoldReportTableColumnDefContext);
